(() => {
  'use strict';

  const controllers = new WeakMap();
  const required = 9;
  const escapeHTML = (value) => String(value).replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);

  class BooBoxBuilder {
    constructor(root, form, designs) {
      this.root = root;
      this.form = form;
      this.designs = designs;
      this.counts = designs.map(() => 0);
      this.events = new AbortController();
      this.media = window.matchMedia('(max-width: 860px)');
      this.summary = this.find('#box-summary');
      this.summaryHost = this.find('#summary-host');
      this.dialog = this.find('#summary-dialog');
      this.dialogMount = this.find('#summary-dialog-mount');
      this.nativeButton = form.querySelector('[name="add"]') || form.querySelector('button[type="submit"]');
      this.guidance = this.find('[data-boo-submit-guidance]') || this.find('#boo-submit-guidance');
      this.recipeProperty = this.property('Cookie designs and quantities (per box)', 'recipe');
      this.sizeProperty = this.property('Cookies per box', 'size');
      this.returnFocus = null;
      this.toastTimer = null;
      this.announceTimer = null;
      this.renderGrid();
      this.listen();
      this.render();
      root.dataset.booInitialized = 'true';
    }

    find(selector) {
      return this.root.querySelector(selector);
    }

    property(name, marker) {
      let input = Array.from(this.form.elements).find((element) => element.type === 'hidden' && element.name === `properties[${name}]`);
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        this.form.append(input);
      }
      input.dataset.booProperty = marker;
      input.name = `properties[${name}]`;
      input.setAttribute('form', this.form.getAttribute('id'));
      input.disabled = true;
      return input;
    }

    total() {
      return this.counts.reduce((sum, count) => sum + count, 0);
    }

    complete() {
      return this.counts.every((count) => Number.isSafeInteger(count) && count >= 0 && count <= required) && this.total() === required;
    }

    available() {
      return this.root.dataset.available !== 'false';
    }

    renderGrid() {
      const grid = this.find('#cookie-grid');
      grid.innerHTML = this.designs.map((design, index) => {
        const name = escapeHTML(design.name);
        const nameId = `${this.form.getAttribute('id')}-boo-design-${index}`;
        return `<article class="cookie-card" data-card="${index}" data-count="0">
          <button class="photo-button" data-action="add" data-index="${index}" type="button" aria-label="Add one ${name} cookie to your box">
            <img src="${escapeHTML(design.image)}" alt="${name} decorated Halloween cookie" width="640" height="640" ${index < 3 ? 'fetchpriority="high"' : 'loading="lazy"'} draggable="false">
            <span class="cookie-selected-count" hidden aria-hidden="true">0</span><span class="image-add" aria-hidden="true">+</span>
          </button>
          <div class="cookie-card-info"><h3 class="cookie-name" id="${escapeHTML(nameId)}">${name}</h3>
            <div class="quantity-control" role="group" aria-labelledby="${escapeHTML(nameId)}">
              <button data-action="remove" data-index="${index}" type="button" aria-label="Remove one ${name} cookie" disabled>−</button>
              <input class="quantity-input" data-index="${index}" type="number" min="0" max="9" step="1" value="0" inputmode="numeric" autocomplete="off" aria-label="Quantity of ${name} cookies">
              <button data-action="add" data-index="${index}" type="button" aria-label="Add one ${name} cookie">+</button>
            </div>
          </div>
        </article>`;
      }).join('');
    }

    render() {
      const total = this.total();
      const remaining = required - total;
      const complete = this.complete();
      this.designs.forEach((design, index) => {
        const card = this.find(`[data-card="${index}"]`);
        const count = this.counts[index];
        card.classList.toggle('selected', count > 0);
        card.dataset.count = String(count);
        const input = card.querySelector('.quantity-input');
        input.value = String(count);
        input.max = String(required - (total - count));
        card.querySelector('[data-action="remove"]').disabled = count === 0;
        card.querySelector('.quantity-control [data-action="add"]').disabled = remaining === 0;
        const photo = card.querySelector('.photo-button');
        photo.setAttribute('aria-disabled', String(remaining === 0));
        photo.setAttribute('aria-label', remaining > 0 ? `Add one ${design.name} cookie to your box. ${count} selected.` : `Box full. ${design.name}: ${count} selected. Remove a cookie before adding another.`);
        const badge = card.querySelector('.cookie-selected-count');
        badge.hidden = count === 0;
        badge.textContent = `×${count}`;
      });
      this.find('#selection-count').innerHTML = `<strong>${total}</strong> of ${required} cookies`;
      const progress = this.find('#selection-progress');
      progress.setAttribute('aria-valuenow', String(total));
      progress.setAttribute('aria-valuetext', `${total} of ${required} cookies selected`);
      this.find('#progress-fill').style.width = `${total / required * 100}%`;
      this.summary.classList.toggle('complete', complete);
      this.summary.dataset.total = String(total);
      this.root.dataset.booComplete = String(complete);
      this.find('#clear-box').disabled = total === 0;
      this.find('#progress-message').textContent = total === 0 ? 'Choose any 9 cookies to fill your box.' : complete ? 'Your box is full. Choose your pickup or delivery details below.' : `${remaining} more ${remaining === 1 ? 'cookie' : 'cookies'} to make it yours.`;

      const slots = [];
      this.designs.forEach((design, index) => {
        for (let item = 0; item < this.counts[index]; item += 1) {
          slots.push(`<button class="box-slot filled" type="button" data-action="remove" data-index="${index}" aria-label="Remove one ${escapeHTML(design.name)} cookie from your box" title="Remove one ${escapeHTML(design.name)}"><img src="${escapeHTML(design.image)}" alt="" width="640" height="640"><span class="remove-mark" aria-hidden="true">×</span></button>`);
        }
      });
      for (let index = total; index < required; index += 1) {
        slots.push(`<div class="box-slot empty" aria-label="Empty cookie space ${index + 1}"><span aria-hidden="true">${index + 1}</span></div>`);
      }
      this.find('#box-slots').innerHTML = slots.join('');
      const list = this.find('#selection-list');
      list.innerHTML = this.designs.map((design, index) => this.counts[index] ? `<div class="selection-row" role="listitem"><span>${escapeHTML(design.name)}</span><span>× ${this.counts[index]}</span></div>` : '').join('');
      list.setAttribute('role', 'list');
      list.setAttribute('aria-label', 'Cookie designs and quantities in each box');
      list.hidden = total === 0;
      const empty = this.find('#empty-selection');
      empty.hidden = total !== 0;
      if (empty.lastChild && empty.lastChild.nodeType === Node.TEXT_NODE) {
        empty.lastChild.textContent = this.media.matches ? 'Choose a design above to get started.' : 'Pick a cookie on the left to get started.';
      }
      if (this.guidance) {
        this.guidance.textContent = complete ? 'Your choice of 9 cookies, in one box.' : total === 0 ? 'Choose 9 cookies to continue.' : `Choose ${remaining} more ${remaining === 1 ? 'cookie' : 'cookies'} to continue.`;
        this.guidance.classList.remove('boo-box-error');
      }
      const mobileCount = this.find('#mobile-count');
      if (mobileCount) mobileCount.textContent = `${total} of 9 cookies`;
      const mobileAction = this.find('#mobile-action');
      if (mobileAction) {
        mobileAction.disabled = !this.available();
        mobileAction.innerHTML = !this.available() ? 'Sold out' : complete ? 'Add to cart <span aria-hidden="true">→</span>' : 'Review box <span aria-hidden="true">→</span>';
        mobileAction.setAttribute('aria-haspopup', 'dialog');
      }
      this.writeProperties();
    }

    writeProperties() {
      const complete = this.complete();
      this.recipeProperty.value = this.designs.flatMap((design, index) => this.counts[index] ? [`${design.name}: ${this.counts[index]}`] : []).join('; ');
      this.sizeProperty.value = String(required);
      this.recipeProperty.disabled = !complete;
      this.sizeProperty.disabled = !complete;
    }

    changeCount(index, requested) {
      if (!Number.isInteger(index) || index < 0 || index >= this.designs.length) return;
      const old = this.counts[index];
      const capacity = required - (this.total() - old);
      const normalized = Number.isFinite(requested) ? Math.max(0, Math.trunc(requested)) : 0;
      const next = Math.min(normalized, capacity);
      if (old === next) {
        this.find(`.quantity-input[data-index="${index}"]`).value = String(next);
        if (normalized > capacity) this.toast('Your box has 9 cookies. Remove one to swap designs.');
        return;
      }
      this.counts[index] = next;
      this.render();
      if (normalized > capacity) this.toast('Your box has 9 cookies. Remove one to swap designs.');
      this.announce(`${this.designs[index].name}: ${next}. ${this.total()} of ${required} cookies selected.${this.complete() ? ' Your box is ready. Choose your pickup or delivery details.' : ''}`);
      this.changed();
    }

    changed() {
      this.root.dispatchEvent(new CustomEvent('boobox:change', { bubbles: true, detail: { totalCookies: this.total(), requiredCookies: required, complete: this.complete(), designs: this.designs.flatMap((design, index) => this.counts[index] ? [{ id: design.id, name: design.name, quantity: this.counts[index] }] : []) } }));
    }

    announce(message) {
      const target = this.find('#announcer');
      if (!target) return;
      clearTimeout(this.announceTimer);
      target.textContent = '';
      this.announceTimer = setTimeout(() => { target.textContent = message; }, 30);
    }

    toast(message) {
      const target = this.find('#toast');
      if (!target) return;
      clearTimeout(this.toastTimer);
      target.textContent = message;
      target.classList.add('visible');
      this.toastTimer = setTimeout(() => target.classList.remove('visible'), 3400);
    }

    showSummary() {
      if (!this.media.matches || !this.dialog || !this.dialogMount || typeof this.dialog.showModal !== 'function') {
        this.summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (this.dialog.open) return;
      this.returnFocus = document.activeElement;
      this.dialogMount.append(this.summary);
      this.dialog.showModal();
      document.body.classList.add('boo-box-dialog-open');
      this.find('#close-summary')?.focus({ preventScroll: true });
    }

    restoreSummary(restoreFocus = true) {
      if (this.summary.parentElement !== this.summaryHost) this.summaryHost.append(this.summary);
      document.body.classList.remove('boo-box-dialog-open');
      if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    closeSummary(restoreFocus = true) {
      if (!restoreFocus) this.returnFocus = null;
      if (this.dialog?.open) this.dialog.close();
      this.restoreSummary(restoreFocus);
    }

    invalid(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const message = !this.available() ? 'This box is currently sold out.' : `Choose exactly 9 cookies before adding your box. You have selected ${this.total()}.`;
      this.showSummary();
      if (this.guidance) {
        this.guidance.textContent = message;
        this.guidance.classList.add('boo-box-error');
        this.guidance.setAttribute('tabindex', '-1');
        this.guidance.focus({ preventScroll: true });
      }
      this.announce(message);
    }

    listen() {
      const options = { signal: this.events.signal };
      this.root.addEventListener('input', (event) => {
        const field = event.target.closest('.quantity-input');
        if (!field || !this.root.contains(field)) return;
        const empty = field.value === '';
        this.changeCount(Number(field.dataset.index), empty ? 0 : Number(field.value));
        if (empty) field.value = '';
      }, options);
      this.root.addEventListener('change', (event) => {
        if (event.target.matches('.quantity-input')) this.changeCount(Number(event.target.dataset.index), Number(event.target.value));
      }, options);
      this.root.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('.quantity-input')) {
          event.preventDefault();
          event.target.blur();
        }
      }, options);
      this.root.addEventListener('click', (event) => {
        const control = event.target.closest('button[data-action]');
        if (control && this.root.contains(control) && !control.disabled) {
          const index = Number(control.dataset.index);
          const slot = control.classList.contains('box-slot');
          this.changeCount(index, this.counts[index] + (control.dataset.action === 'add' ? 1 : -1));
          if (slot) (this.find(`.box-slot.filled[data-index="${index}"]`) || this.find('.box-slot.filled') || (this.dialog?.open ? this.find('#close-summary') : this.find(`.photo-button[data-index="${index}"]`)))?.focus({ preventScroll: true });
        }
        const button = event.target.closest('button');
        if (!button || !this.root.contains(button)) return;
        if (button.id === 'clear-box') {
          this.counts.fill(0);
          this.render();
          this.announce('Your box has been cleared. Choose any 9 cookies.');
          this.changed();
        } else if (button.id === 'mobile-view-box') {
          this.showSummary();
        } else if (button.id === 'close-summary') {
          this.closeSummary();
        } else if (button.id === 'mobile-action') {
          if (!this.complete() || !this.nativeButton || this.nativeButton.disabled || this.nativeButton.getAttribute('aria-disabled') === 'true') {
            this.showSummary();
          } else {
            this.closeSummary(false);
            this.summary.scrollIntoView({ behavior: 'smooth', block: 'start' });
            this.nativeButton.click();
          }
        }
      }, options);
      document.addEventListener('click', (event) => {
        if (!this.root.isConnected || !this.nativeButton || !this.nativeButton.contains(event.target)) return;
        if (!this.complete() || !this.available()) this.invalid(event);
        else {
          this.writeProperties();
          this.closeSummary(false);
        }
      }, { capture: true, signal: this.events.signal });
      document.addEventListener('submit', (event) => {
        if (event.target !== this.form || !this.root.isConnected) return;
        if (!this.complete() || !this.available()) this.invalid(event);
        else {
          this.writeProperties();
          this.closeSummary(false);
        }
      }, { capture: true, signal: this.events.signal });
      this.dialog?.addEventListener('close', () => this.restoreSummary(), options);
      this.dialog?.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeSummary();
      }, options);
      this.media.addEventListener('change', () => {
        if (!this.media.matches) this.closeSummary(false);
        this.render();
      }, options);
    }

    destroy() {
      this.closeSummary(false);
      this.events.abort();
      clearTimeout(this.toastTimer);
      clearTimeout(this.announceTimer);
      controllers.delete(this.root);
      delete this.root.dataset.booInitialized;
    }
  }

  function initialize(scope = document) {
    const roots = [...scope.querySelectorAll('.boo-box-builder')];
    if (scope.matches?.('.boo-box-builder')) roots.unshift(scope);
    roots.forEach((root) => {
      if (controllers.has(root) || root.dataset.booInitialized === 'true') return;
      const form = document.getElementById(root.dataset.formId);
      const source = root.querySelector('script[type="application/json"][data-boo-designs]');
      if (!form || !source) return;
      let designs;
      try { designs = JSON.parse(source.textContent); } catch (_) { return; }
      if (!Array.isArray(designs) || designs.length === 0 || !designs.every((design) => design && typeof design.id === 'string' && typeof design.name === 'string' && typeof design.image === 'string')) return;
      controllers.set(root, new BooBoxBuilder(root, form, designs));
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('.boo-box-builder').forEach((root) => controllers.get(root)?.destroy());
  });
})();
